/**
 * The assets drawer, like Unreal's content drawer: the "Assets" button of the status bar
 * (`StatusBar.tsx`, or Ctrl+Space, command `presets.drawer`) slides the assets browser (files
 * and presets) up over the workspace. A click outside it, Escape, or placing a preset or a file
 * on the graph closes it; dragging a card out of it onto the graph works while it is open.
 * "Dock in layout" turns it into the Assets window instead.
 */
import { Show, createEffect, onCleanup, type JSX } from "solid-js";
import { useApp } from "./context";
import { PresetBrowser } from "../windows/presets/PresetBrowser";

/** The drawer (its button is in the status bar). */
export function PresetDrawer(): JSX.Element {
  const { presets, assets, wm } = useApp();
  let panel: HTMLDivElement | undefined;

  // Closes on a press outside it (the button toggles it itself, menus and the ghost are outside too).
  createEffect(() => {
    if (!presets.drawer()) return;
    const outside = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target || panel?.contains(target) || (target as Element).closest?.(".ctx-menu, #preset-drawer-button")) return;
      presets.setDrawer(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || presets.draft()) return;
      if (assets.previewed()) assets.preview(null);
      else presets.setDrawer(false);
    };
    addEventListener("pointerdown", outside, true);
    addEventListener("keydown", escape);
    onCleanup(() => {
      removeEventListener("pointerdown", outside, true);
      removeEventListener("keydown", escape);
    });
  });

  return (
    <>
      <Show when={presets.drawer()}>
        <div class="pdrawer" id="preset-drawer" ref={panel} role="dialog" aria-label="Assets">
          <PresetBrowser
            onPlaced={() => presets.setDrawer(false)}
            tools={
              <button
                type="button"
                class="mini"
                id="preset-dock"
                title="Show the assets as a window in the layout"
                onClick={() => {
                  presets.setDrawer(false);
                  wm.open("presets");
                }}
              >
                Dock in layout
              </button>
            }
          />
        </div>
      </Show>
    </>
  );
}
